/**
 * Build identity for error and telemetry reports (#4022).
 *
 * THE DEFECT THIS CLOSES. Every client already generates a real build stamp:
 * `scripts/app-version/gen-version.mjs` runs as a `prebuild` hook in ALL FOUR
 * PWAs and writes `{ version, sha, builtAt, app }` to
 * `<app>/src/generated/appVersion.json`. None of it reached the error reports.
 *
 *   - `client` passed `release: process.env.REACT_APP_SENTRY_RELEASE || 'local'`.
 *     That variable is referenced in exactly ONE place in the repo and is SET
 *     NOWHERE -- no CI step, no .env, no build script. So the release on every
 *     production report was the literal string 'local'. A constant, not a value:
 *     it cannot vary, so it can never distinguish one build from another, and an
 *     error could not be tied to a release.
 *   - `sales-client` passed no `release` at all.
 *
 * This module is the single place that turns the baked stamp into report
 * identity, so the four clients cannot drift into four spellings of it.
 *
 * DELIBERATELY DEPENDENCY-FREE. It imports a TYPE and nothing else. The three
 * Vite PWAs alias `@shared` -> `client/src`, and a bare specifier imported by
 * shared code must be a dependency of EVERY consumer's own package.json or Vite
 * externalises it and the app crashes at runtime inside a lazy chunk -- while
 * the build stays green (see the 2026-07-30 `piexifjs` crash on
 * technician.onpointgen.com). Keeping `@sentry/react` out of this file means
 * each client imports the SDK from its own package.json and shared code adds no
 * new runtime specifier to anyone.
 */
import type { VersionInfo } from '../../appVersion/types';

/** Fallback when a build somehow ships without a usable stamp. */
const UNKNOWN = 'unknown';

function clean(value: string | undefined | null, fallback: string): string {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed.length > 0 ? trimmed : fallback;
}

/**
 * The Sentry `release` for a build: `<app>@<version>+<sha>`.
 *
 * Includes the app name because all four PWAs report into the same Sentry
 * project -- without it, two apps built the same day are indistinguishable. The
 * `sha` is the exact build-identity key and is what makes a report traceable to
 * a commit; `version` is the human-readable date-based build.
 */
export function buildSentryRelease(info: Partial<VersionInfo> | undefined | null): string {
  const app = clean(info?.app, UNKNOWN);
  const version = clean(info?.version, UNKNOWN);
  const sha = clean(info?.sha, UNKNOWN);
  return `${app}@${version}+${sha}`;
}

/**
 * Tags attached to every report, so a build can be filtered on in the Sentry UI
 * without parsing the release string.
 */
export function buildVersionTags(
  info: Partial<VersionInfo> | undefined | null,
): Record<string, string> {
  return {
    app: clean(info?.app, UNKNOWN),
    app_version: clean(info?.version, UNKNOWN),
    build_sha: clean(info?.sha, UNKNOWN),
    built_at: clean(info?.builtAt, UNKNOWN),
  };
}

/**
 * True when the stamp is real rather than the checked-in placeholder.
 *
 * The committed `appVersion.json` files carry `sha: "dev"`, `builtAt` at the
 * epoch, and version `1.0.0` -- the generator overwrites them at build time. A
 * production bundle reporting the placeholder means the `prebuild` hook did not
 * run, which is worth knowing rather than silently reporting a fake identity.
 */
export function isRealBuildStamp(info: Partial<VersionInfo> | undefined | null): boolean {
  if (!info) return false;
  const sha = clean(info.sha, '');
  const builtAt = clean(info.builtAt, '');
  if (sha === '' || sha === 'dev' || sha === UNKNOWN) return false;
  if (builtAt === '' || builtAt.startsWith('1970-01-01')) return false;
  return true;
}
